import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Medal,
  Trophy,
  Globe,
  LogOut,
  User,
  Crown,
  TrendingUp,
  Hash,
  CalendarDays,
  Check
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

const formatScore = (score) => {
  if (score === 0) return 'E';
  if (score > 0) return `+${score}`;
  return String(score);
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500/20 text-amber-400 font-mono font-bold text-sm">1</span>;
  if (rank === 2) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-300/20 text-gray-300 font-mono font-bold text-sm">2</span>;
  if (rank === 3) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-orange-600/20 text-orange-500 font-mono font-bold text-sm">3</span>;
  return <span className="inline-flex items-center justify-center w-7 h-7 text-muted-foreground font-mono text-sm">{rank}</span>;
};

const RankingTable = ({ rankings, title, icon, emptyText, highlightMemberId }) => (
  <Card className="bg-card border-white/10 flex-1">
    <CardHeader className="pb-3">
      <CardTitle className="font-heading text-lg flex items-center gap-2">
        {icon}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      {rankings.length === 0 ? (
        <div className="px-6 pb-6 text-center">
          <p className="text-muted-foreground text-sm py-8">{emptyText}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid={`ranking-table-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <thead>
              <tr className="border-t border-b border-white/10 text-xs text-muted-foreground uppercase tracking-wider">
                <th className="px-4 py-3 text-left w-14">Rank</th>
                <th className="px-4 py-3 text-left">Player</th>
                <th className="px-4 py-3 text-center hidden sm:table-cell">Played</th>
                <th className="px-4 py-3 text-center">Avg</th>
                <th className="px-4 py-3 text-center hidden sm:table-cell">Best</th>
              </tr>
            </thead>
            <tbody>
              {rankings.map((player, i) => (
                <tr
                  key={player.member_id}
                  className={`border-b border-white/5 transition-colors ${
                    player.member_id === highlightMemberId
                      ? 'bg-primary/10'
                      : i % 2 === 0 ? 'bg-white/[0.02]' : ''
                  }`}
                  data-testid={`ranking-row-${i}`}
                >
                  <td className="px-4 py-3">
                    <RankBadge rank={player.rank} />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className={`text-sm font-medium ${player.member_id === highlightMemberId ? 'text-primary' : 'text-white'}`}>
                        {player.member_name}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground font-mono">{player.member_number}</span>
                        {player.membership_tier === 'pro' && (
                          <Crown className="w-3 h-3 text-amber-400" />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    <span className="text-sm text-muted-foreground">{player.tournaments_played}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-mono font-medium ${
                      player.avg_score_to_par < 0 ? 'text-green-400' :
                      player.avg_score_to_par === 0 ? 'text-white' :
                      'text-red-400'
                    }`}>
                      {formatScore(player.avg_score_to_par)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    <span className={`text-sm font-mono ${
                      player.best_score_to_par < 0 ? 'text-green-400' :
                      player.best_score_to_par === 0 ? 'text-white' :
                      'text-red-400'
                    }`}>
                      {formatScore(player.best_score_to_par)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

export const RankingsPage = () => {
  const navigate = useNavigate();
  const { member, logout } = useAuth();
  const [nationalRankings, setNationalRankings] = useState([]);
  const [tourRankings, setTourRankings] = useState([]);
  const [tours, setTours] = useState([]);
  const [selectedTourId, setSelectedTourId] = useState('');
  const [selectedTourName, setSelectedTourName] = useState('');
  const [loadingNational, setLoadingNational] = useState(true);
  const [loadingTour, setLoadingTour] = useState(false);
  
  // Season state
  const [viewMode, setViewMode] = useState('all-time'); // 'all-time' or 'season'
  const [activeSeason, setActiveSeason] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    fetchTours();
    fetchActiveSeason();
  }, []);

  useEffect(() => {
    fetchNationalRankings();
    if (selectedTourId && selectedTourId !== 'none') {
      fetchTourRankings(selectedTourId);
    }
  }, [viewMode, activeSeason]);

  const fetchActiveSeason = async () => {
    try {
      const response = await axios.get(`${API_URL}/seasons/active`);
      if (response.data) {
        setActiveSeason(response.data);
        checkRegistration(response.data.id);
      }
    } catch (error) {
      // No active season
    }
  };

  const checkRegistration = async (seasonId) => {
    if (!member?.id) return;
    try {
      const response = await axios.get(`${API_URL}/seasons/${seasonId}/check-registration?member_id=${member.id}`);
      setIsRegistered(response.data.registered);
    } catch (error) {
      // ignore
    }
  };

  const handleRegister = async () => {
    if (!activeSeason) return;
    setRegistering(true);
    try {
      await axios.post(`${API_URL}/seasons/${activeSeason.id}/register?member_id=${member.id}`);
      setIsRegistered(true);
      toast.success(`Registered for ${activeSeason.name}!`);
      // Refresh rankings if viewing season
      if (viewMode === 'season') {
        fetchNationalRankings();
        if (selectedTourId && selectedTourId !== 'none') {
          fetchTourRankings(selectedTourId);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setRegistering(false);
    }
  };

  const fetchNationalRankings = async () => {
    setLoadingNational(true);
    try {
      let url = `${API_URL}/rankings/national`;
      if (viewMode === 'season' && activeSeason) {
        url += `?season_id=${activeSeason.id}`;
      }
      const response = await axios.get(url);
      setNationalRankings(response.data);
    } catch (error) {
      toast.error('Failed to load national rankings');
    } finally {
      setLoadingNational(false);
    }
  };

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API_URL}/tours`);
      setTours(response.data);
    } catch (error) {
      // ignore
    }
  };

  const fetchTourRankings = async (tourId) => {
    if (!tourId) {
      setTourRankings([]);
      return;
    }
    setLoadingTour(true);
    try {
      let url = `${API_URL}/rankings/tour/${tourId}`;
      if (viewMode === 'season' && activeSeason) {
        url += `?season_id=${activeSeason.id}`;
      }
      const response = await axios.get(url);
      setTourRankings(response.data.rankings);
      setSelectedTourName(response.data.tour?.name || '');
    } catch (error) {
      toast.error('Failed to load tour rankings');
      setTourRankings([]);
    } finally {
      setLoadingTour(false);
    }
  };

  const handleTourChange = (value) => {
    setSelectedTourId(value);
    if (value === 'none') {
      setTourRankings([]);
      setSelectedTourName('');
    } else {
      fetchTourRankings(value);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const myNationalRank = nationalRankings.find(r => r.member_id === member?.id);
  const myTourRank = tourRankings.find(r => r.member_id === member?.id);

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img
              src={TGA_LOGO}
              alt="TGA Pro Tour"
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-logo"
            />
            <div className="hidden sm:flex items-center gap-1">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-white text-sm" data-testid="nav-dashboard">Dashboard</Button>
              <Button variant="ghost" onClick={() => navigate('/schedule')} className="text-muted-foreground hover:text-white text-sm" data-testid="nav-schedule">Schedule</Button>
              <Button variant="ghost" onClick={() => navigate('/tours')} className="text-muted-foreground hover:text-white text-sm" data-testid="nav-tours">Tours</Button>
              <Button variant="ghost" className="text-white text-sm border-b-2 border-primary rounded-none" data-testid="nav-rankings-active">Rankings</Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {member?.is_admin && (
              <span className="px-2 py-1 rounded bg-primary/20 text-primary text-xs font-medium border border-primary/50">Admin</span>
            )}
            <Button variant="ghost" onClick={() => navigate('/profile')} className="text-muted-foreground hover:text-white" data-testid="nav-profile-btn">
              <User className="mr-2 h-4 w-4" /><span className="hidden sm:inline">{member?.name}</span>
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground hover:text-destructive" data-testid="nav-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Medal className="w-8 h-8 text-primary" />
            <h1 className="text-3xl sm:text-4xl font-heading text-white">Player Rankings</h1>
          </div>
          <p className="text-muted-foreground">
            Rankings based on average score-to-par across tournaments played
          </p>
        </div>

        {/* Season Banner + Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          {/* Toggle */}
          <div className="flex items-center bg-card border border-white/10 rounded-lg p-1" data-testid="ranking-view-toggle">
            <button
              onClick={() => setViewMode('all-time')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'all-time'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-white'
              }`}
              data-testid="toggle-all-time"
            >
              <Trophy className="w-4 h-4 inline mr-2" />
              All-Time
            </button>
            <button
              onClick={() => activeSeason ? setViewMode('season') : toast.info('No active season')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'season'
                  ? 'bg-primary text-white'
                  : activeSeason ? 'text-muted-foreground hover:text-white' : 'text-muted-foreground/50 cursor-not-allowed'
              }`}
              data-testid="toggle-season"
            >
              <CalendarDays className="w-4 h-4 inline mr-2" />
              {activeSeason ? activeSeason.name : 'Season'}
            </button>
          </div>

          {/* Season Registration */}
          {activeSeason && viewMode === 'season' && !isRegistered && (
            <Button
              onClick={handleRegister}
              disabled={registering}
              className="bg-primary hover:bg-primary/90 rounded-pill px-6 btn-hover-scale"
              data-testid="register-season-btn"
            >
              {registering ? 'Registering...' : `Register for ${activeSeason.name}`}
            </Button>
          )}
          {activeSeason && viewMode === 'season' && isRegistered && (
            <span className="flex items-center gap-2 text-sm text-green-400">
              <Check className="w-4 h-4" />
              Registered for {activeSeason.name}
            </span>
          )}
        </div>

        {/* My Rank Summary */}
        {myNationalRank && (
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/20">
              <Hash className="w-4 h-4 text-primary" />
              <span className="text-sm text-white">
                {viewMode === 'season' ? 'Season' : 'National'} Rank: <strong className="text-primary font-mono">#{myNationalRank.rank}</strong>
              </span>
              <span className="text-xs text-muted-foreground">({formatScore(myNationalRank.avg_score_to_par)} avg)</span>
            </div>
            {myTourRank && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Globe className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-white">Tour Rank: <strong className="text-amber-400 font-mono">#{myTourRank.rank}</strong></span>
                <span className="text-xs text-muted-foreground">({formatScore(myTourRank.avg_score_to_par)} avg)</span>
              </div>
            )}
          </div>
        )}

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* National Rankings */}
          {loadingNational ? (
            <Card className="bg-card border-white/10 flex-1">
              <CardContent className="p-12 text-center">
                <div className="animate-pulse text-primary">Loading rankings...</div>
              </CardContent>
            </Card>
          ) : (
            <RankingTable
              rankings={nationalRankings}
              title={viewMode === 'season' && activeSeason ? `${activeSeason.name} — National` : 'National Rank'}
              icon={<TrendingUp className="w-5 h-5 text-primary" />}
              emptyText={viewMode === 'season' ? 'No scores submitted in this season yet.' : 'No players have submitted scores yet.'}
              highlightMemberId={member?.id}
            />
          )}

          {/* Tour Rankings */}
          <div className="flex flex-col gap-4">
            <Card className="bg-card border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <Select value={selectedTourId || 'none'} onValueChange={handleTourChange}>
                    <SelectTrigger className="bg-muted border-border flex-1" data-testid="tour-rank-selector">
                      <SelectValue placeholder="Select a tour..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="none"><span className="text-muted-foreground">Select a tour...</span></SelectItem>
                      {tours.map(tour => (
                        <SelectItem key={tour.id} value={tour.id} data-testid={`tour-rank-option-${tour.id}`}>
                          {tour.name} ({tour.region})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {loadingTour ? (
              <Card className="bg-card border-white/10 flex-1">
                <CardContent className="p-12 text-center">
                  <div className="animate-pulse text-amber-400">Loading tour rankings...</div>
                </CardContent>
              </Card>
            ) : selectedTourId && selectedTourId !== 'none' ? (
              <RankingTable
                rankings={tourRankings}
                title={viewMode === 'season' && activeSeason ? `${selectedTourName} — ${activeSeason.name}` : `${selectedTourName} Rank`}
                icon={<Globe className="w-5 h-5 text-amber-400" />}
                emptyText={viewMode === 'season' ? 'No scores in this tour for the current season.' : 'No players have completed tournaments in this tour yet.'}
                highlightMemberId={member?.id}
              />
            ) : (
              <Card className="bg-card border-white/10 flex-1">
                <CardContent className="p-12 text-center">
                  <Globe className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">Select a tour above to see tour-specific rankings</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
